import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';

const INDUSTRIES = [
  { value: 'tech',          nl: 'Technologie',       en: 'Technology' },
  { value: 'healthcare',    nl: 'Zorg',               en: 'Healthcare' },
  { value: 'finance',       nl: 'Financiën',          en: 'Finance' },
  { value: 'education',     nl: 'Onderwijs',          en: 'Education' },
  { value: 'manufacturing', nl: 'Productie',          en: 'Manufacturing' },
  { value: 'retail',        nl: 'Retail',             en: 'Retail' },
  { value: 'logistics',     nl: 'Logistiek',          en: 'Logistics' },
  { value: 'general',       nl: 'Algemeen / Overig',  en: 'General / Other' },
];

export default function BusinessROIScreen({ lang, businessId }) {
  const nl = lang === 'nl';
  const [roi, setRoi] = useState(null);
  const [bench, setBench] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [cfg, setCfg] = useState({ avg_salary: 45000, sick_day_cost: 250, industry: 'general' });
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState(false);

  const BASE = process.env.REACT_APP_API_URL || '/api';

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/businesses/${businessId}/roi`).then(r => r.json()),
      fetch(`${BASE}/businesses/${businessId}/benchmarks`).then(r => r.json()),
    ]).then(([roiData, benchData]) => {
      setRoi(roiData);
      setBench(benchData);
      if (roiData.config) {
        setCfg({
          avg_salary:    roiData.config.avg_salary    ?? 45000,
          sick_day_cost: roiData.config.sick_day_cost ?? 250,
          industry:      roiData.config.industry      ?? 'general',
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [businessId, BASE]);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    setSaveErr(false);
    fetch(`${BASE}/businesses/${businessId}/roi`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cfg),
    }).then(r => r.json()).then(() => {
      setSaved(true);
      setTimeout(() => { setSaved(false); setEditing(false); load(); }, 1200);
    }).catch(() => { setSaveErr(true); });
  };

  const fmt    = (n) => n != null ? '€' + Number(n).toLocaleString('nl-NL', { maximumFractionDigits: 0 }) : '—';
  const fmtNum = (n) => n != null ? Number(n).toLocaleString('nl-NL', { maximumFractionDigits: 1 }) : '—';

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: C.silver, fontSize: 13 }}>...</div>;

  const benchData = bench?.benchmark;
  const industry  = bench?.industry ?? 'general';
  const indLabel  = INDUSTRIES.find(i => i.value === industry)?.[nl ? 'nl' : 'en'] ?? industry;

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>ROI Dashboard</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
            {nl ? 'Rendement op welzijnsinvestering' : 'Wellness investment returns'}
          </div>
        </div>
        <button onClick={() => setEditing(!editing)}
          style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
          {editing ? (nl ? 'Annuleren' : 'Cancel') : (nl ? 'Configureren' : 'Configure')}
        </button>
      </div>

      {/* Config panel */}
      {editing && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {nl ? 'Bedrijfsconfiguratie' : 'Company Configuration'}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={{ color: C.silver, fontSize: 12 }}>
              {nl ? 'Gemiddeld jaarsalaris (€)' : 'Average salary (€/year)'}
              <input type="number" value={cfg.avg_salary}
                onChange={e => setCfg(p => ({ ...p, avg_salary: parseFloat(e.target.value) || 45000 }))}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 8, border: `1px solid ${_T.cardBorderBot}`, background: _T.bg4, color: C.white, fontSize: 14, boxSizing: 'border-box' }} />
            </label>
            <label style={{ color: C.silver, fontSize: 12 }}>
              {nl ? 'Kosten per ziektedag (€)' : 'Cost per sick day (€)'}
              <input type="number" value={cfg.sick_day_cost}
                onChange={e => setCfg(p => ({ ...p, sick_day_cost: parseFloat(e.target.value) || 250 }))}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 8, border: `1px solid ${_T.cardBorderBot}`, background: _T.bg4, color: C.white, fontSize: 14, boxSizing: 'border-box' }} />
            </label>
            <label style={{ color: C.silver, fontSize: 12 }}>
              {nl ? 'Sector' : 'Industry'}
              <select value={cfg.industry} onChange={e => setCfg(p => ({ ...p, industry: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 8, border: `1px solid ${_T.cardBorderBot}`, background: _T.bg4, color: C.white, fontSize: 14, boxSizing: 'border-box' }}>
                {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{nl ? i.nl : i.en}</option>)}
              </select>
            </label>
          </div>
          {saveErr && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{nl ? 'Opslaan mislukt, probeer opnieuw.' : 'Save failed, please try again.'}</div>}
          <button onClick={save}
            style={{ marginTop: 12, background: saved ? `${C.teal}20` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: saved ? `1px solid ${C.teal}` : 'none', borderRadius: 10, color: saved ? C.teal : '#0a1220', fontSize: 13, fontWeight: 700, padding: '10px 20px', cursor: 'pointer', width: '100%' }}>
            {saved ? `✓ ${nl ? 'Opgeslagen' : 'Saved'}` : (nl ? 'Configuratie opslaan' : 'Save Configuration')}
          </button>
        </div>
      )}

      {/* Main ROI card */}
      <div style={{ background: 'linear-gradient(135deg,#1a3a5c,#0d2744)', border: '1px solid #2a5080', borderRadius: 16, padding: 20, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: '#8ab4d4', marginBottom: 4 }}>
          {nl ? 'Geschatte besparing t.o.v. sectorgemiddelde' : 'Estimated savings vs. industry benchmark'}
        </div>
        <div style={{ fontSize: 34, fontWeight: 800, color: C.white, marginBottom: 4 }}>{fmt(roi?.saved_amount_eur)}</div>
        <div style={{ fontSize: 12, color: '#8ab4d4' }}>
          {roi?.saved_days ?? 0} {nl ? 'minder ziektedagen' : 'fewer sick days'} vs. benchmark ({fmtNum(roi?.benchmark_days)} {nl ? 'verwacht voor' : 'expected for'} {roi?.member_count ?? 0} {nl ? 'medewerkers' : 'people'})
        </div>
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        {[
          { label: nl ? 'Ziektedagen (YTD)' : 'Absence days (YTD)',           value: roi?.absence_days_ytd ?? 0,     sub: nl ? 'werkelijk' : 'actual' },
          { label: nl ? 'Benchmarkdagen' : 'Benchmark days',                   value: fmtNum(roi?.benchmark_days),    sub: indLabel + (nl ? ' gem.' : ' avg') },
          { label: nl ? 'Platformkosten (jaar)' : 'Platform cost (annual)',    value: fmt(roi?.cost_per_seat_annual), sub: nl ? 'abonnement' : 'subscription' },
          { label: nl ? 'ROI multiplier' : 'ROI multiplier',                   value: roi?.saved_amount_eur > 0 ? (roi.saved_amount_eur / (roi.cost_per_seat_annual || 1)).toFixed(1) + 'x' : '—', sub: nl ? 'rendement' : 'return on investment' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: _T.bg3, borderRadius: 12, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ fontSize: 10, color: C.silver, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{kpi.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.white }}>{kpi.value}</div>
            <div style={{ fontSize: 10, color: C.silver2, marginTop: 2 }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Industry benchmark comparison */}
      {benchData && (
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 14 }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {nl ? 'Sectorvergelijking' : 'Industry Benchmarks'} — {indLabel}
          </div>
          {[
            { label: nl ? 'Gem. slaap (uur)' : 'Avg sleep (hrs)',               value: fmtNum(benchData.avg_sleep) },
            { label: nl ? 'Gem. stemming (1-5)' : 'Avg mood (1-5)',             value: fmtNum(benchData.avg_mood) },
            { label: nl ? 'Ziektedagen/persoon/jaar' : 'Sick days/person/year', value: fmtNum(benchData.sick_days_year) },
            { label: nl ? 'Burn-outpercentage' : 'Burnout rate',                value: benchData.burnout_rate != null ? (benchData.burnout_rate * 100).toFixed(0) + '%' : '—' },
          ].map((row, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 3 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
              <span style={{ fontSize: 13, color: C.silver }}>{row.label}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.white }}>{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Business case */}
      <div style={{ background: `${C.gold}12`, borderRadius: 14, padding: 16, border: `1px solid ${C.gold}40` }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: C.gold }}>
          {nl ? 'Business Case' : 'Business Case'}
        </div>
        <div style={{ fontSize: 12, color: '#c8a84b99', lineHeight: 1.7 }}>
          {nl
            ? <>Burn-out en stressgerelateerd verzuim kost Nederlandse bedrijven gemiddeld <strong style={{ color: C.gold }}>€250–€400 per ziektedag</strong>. Met {roi?.member_count ?? 0} medewerkers kan een vermindering van zelfs 20% in ziektedagen {fmt((roi?.benchmark_days ?? 0) * 0.2 * (cfg.sick_day_cost || 250))}/jaar besparen. Het SenseLifting-platform kost {fmt(roi?.cost_per_seat_annual)} per jaar — al terugverdiend met slechts {Math.max(1, Math.ceil((roi?.cost_per_seat_annual || 1800) / (cfg.sick_day_cost || 250)))} minder ziektedagen.</>
            : <>Burnout and stress-related absenteeism costs Dutch companies an average of <strong style={{ color: C.gold }}>€250–€400 per sick day</strong>. With {roi?.member_count ?? 0} team members, reducing sick days by 20% saves {fmt((roi?.benchmark_days ?? 0) * 0.2 * (cfg.sick_day_cost || 250))}/year. SenseLifting at {fmt(roi?.cost_per_seat_annual)}/year pays for itself with just {Math.max(1, Math.ceil((roi?.cost_per_seat_annual || 1800) / (cfg.sick_day_cost || 250)))} fewer sick days.</>
          }
        </div>
      </div>
    </div>
  );
}
